import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef } from "react";

import { api } from "../api/client";

/**
 * Bump the user's lastSeen[category] on the server, then refresh the
 * notifications query so the sidebar badge clears.
 *
 * Usage:
 *   const feed = useQuery(...);
 *   useMarkSeen("intel", feed.dataUpdatedAt);
 *
 * The trigger is `dataUpdatedAt` so we mark seen exactly when fresh data
 * has actually rendered — not just when the page first mounts and the
 * loader is showing. We also debounce so re-renders don't flood the API.
 */
export function useMarkSeen(category: string, trigger: number | undefined) {
  const qc = useQueryClient();
  const lastFiredFor = useRef<number | undefined>(undefined);

  useEffect(() => {
    if (!trigger || trigger === lastFiredFor.current) return;
    lastFiredFor.current = trigger;
    api(`/me/seen/${category}`, { method: "POST" })
      .then(() => qc.invalidateQueries({ queryKey: ["notifications"] }))
      .catch(() => {
        // Best-effort — a failed seen-bump just means the badge stays a
        // bit longer, not a user-visible error.
      });
  }, [category, trigger, qc]);
}
